import { useState, useMemo, type FormEvent } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useSession } from '../../lib/auth/session.js';
import { useExtensions, useCreateExtension, useDeleteExtension } from '../../lib/api/hooks.js';
import { useAdminGroups } from '../../lib/api/hooks-extension.js';
import { useRealtime } from '../../lib/api/realtime.js';
import {
  StatusBadge, TableSkeleton, Empty, ErrorBanner, Badge, Stat, StatGroup,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { ApiClientError } from '../../lib/api/client.js';
import { relativeTime } from '../../lib/format.js';
import { ExtensionDetail } from '../manage/ExtensionDetail.js';
import type { Extension } from '@arcnet/contracts';

/**
 * The seat list.
 *
 * Rebuilt around the question people actually open it with, which is not "what
 * is extension 1004's caller ID" — it is "who is online, and who should be but
 * isn't". Presence therefore leads, and the columns that used to fill the
 * width (caller ID, max concurrent calls) moved to the seat's own page, where
 * somebody looking for them is already going.
 *
 * Reused in two places: standalone, and as a tab inside an account. `tenantId`
 * is passed explicitly when embedded so the tab always shows the account in the
 * URL rather than whatever tenant happens to be selected elsewhere — otherwise
 * a support link could open the wrong customer's data.
 */

type Presence = 'all' | 'online' | 'offline';

export function ExtensionsPage({ tenantId: propTenantId, embedded }: {
  tenantId?: string;
  embedded?: boolean;
} = {}) {
  const activeTenantId = useSession((s) => s.activeTenantId);
  const tenantId = propTenantId ?? activeTenantId;

  /**
   * The open seat lives in the URL.
   *
   * It was component state, which meant no link could name a seat — the Live
   * page's drill-in already passed `?ext=` and nothing read it, so clicking a
   * row landed on the list rather than the person. Support links have the same
   * problem in reverse: "open extension 1004" was a sentence, not a link.
   */
  const search = useSearch({ strict: false }) as { ext?: string };
  const nav = useNavigate();
  const selected = search.ext ?? null;
  const setSelected = (id: string | null): void => {
    void nav({
      to: '.',
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        if (id) next['ext'] = id; else delete next['ext'];
        return next;
      },
      // Opening a seat is navigation the back button should undo; closing it
      // replaces, so back does not bounce between open and shut.
      replace: id === null,
    });
  };
  const can = useSession((s) => s.can);
  const [q, setQ] = useState('');
  const [presence, setPresence] = useState<Presence>('all');
  const [group, setGroup] = useState('');
  const [adding, setAdding] = useState(false);

  // Live for as long as the list is open: a phone registering moves a row from
  // offline to online in about a second, without a refresh.
  const live = useRealtime(tenantId ?? null);
  const { data, isLoading, error } = useExtensions(tenantId, { q: q || undefined, limit: 200 });
  const groups = useAdminGroups(tenantId ?? '');
  const del = useDeleteExtension(tenantId ?? '');

  const all = useMemo(() => data?.data ?? [], [data]);
  const counts = useMemo(() => {
    let online = 0, never = 0;
    for (const e of all) {
      if (e.registration?.registered) online++;
      else if (!e.registration) never++;
    }
    return { total: all.length, online, offline: all.length - online, never };
  }, [all]);

  const rows = useMemo(() => all.filter((e) => {
    if (presence === 'online' && !e.registration?.registered) return false;
    if (presence === 'offline' && e.registration?.registered) return false;
    if (group && e.admin_group_id !== group) return false;
    return true;
  }), [all, presence, group]);

  if (!tenantId) {
    return <Empty icon="accounts" title="No tenant selected"
      hint="Choose a tenant to manage its extensions." />;
  }

  // The detail view replaces the list rather than opening beside it: a seat has
  // six tabs of settings and needs the width.
  if (selected) {
    return (
      <ExtensionDetail tenantId={tenantId} extensionId={selected}
        onBack={() => setSelected(null)} />
    );
  }

  return (
    <>
      <div className="arc-page-head">
        <div>
          {!embedded && <h1 className="arc-h1">Extensions</h1>}
          {!embedded && <p>Seats, their handsets and who is online</p>}
        </div>
        <div className="arc-row">
          {live === 'live' && (
            <Badge tone="ok"><span className="arc-dot" aria-hidden="true" />live</Badge>
          )}
          {can('extension:write') && (
            <button type="button" className="arc-btn arc-btn-primary"
              onClick={() => setAdding((v) => !v)}>
              <Icon name={adding ? 'close' : 'plus'} size={13} />
              {adding ? 'Cancel' : 'New extension'}
            </button>
          )}
        </div>
      </div>

      {error && <ErrorBanner error={error} />}
      {del.error && <ErrorBanner error={del.error} />}
      {adding && <NewExtensionForm tenantId={tenantId} onDone={() => setAdding(false)} />}

      <div style={{ marginBottom: 14 }}>
        <StatGroup>
          <Stat bare icon="extension" hue={1} label="Seats" value={counts.total}
            sub={groups.data?.data.length ? `${groups.data.data.length} cost centres` : 'in this account'} />
          <Stat bare icon="phone" hue={2} label="Online" value={counts.online}
            tone={counts.total > 0 && counts.online === 0 ? 'danger' : undefined}
            sub="handset registered" />
          <Stat bare icon="warning" hue={3} label="Offline" value={counts.offline}
            sub={counts.offline > 0 ? 'cannot receive calls' : 'all seats up'} />
          {/* A seat no node has ever reported on is a different problem from one
              that went offline: usually a handset that was never configured. */}
          <Stat bare icon="server" hue={4} label="Never seen" value={counts.never}
            sub={counts.never > 0 ? 'no handset yet' : 'all provisioned'} />
        </StatGroup>
      </div>

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search number or name…"
              value={q} onChange={(e) => setQ(e.target.value)}
              aria-label="Search extensions" />
          </div>

          <select className="arc-input" style={{ maxWidth: 150 }} value={presence}
            aria-label="Filter by presence"
            onChange={(e) => setPresence(e.target.value as Presence)}>
            <option value="all">All seats</option>
            <option value="online">Online only</option>
            <option value="offline">Offline only</option>
          </select>

          {(groups.data?.data.length ?? 0) > 0 && (
            <select className="arc-input" style={{ maxWidth: 190 }} value={group}
              aria-label="Filter by cost centre"
              onChange={(e) => setGroup(e.target.value)}>
              <option value="">All cost centres</option>
              {groups.data?.data.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          )}

          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {rows.length === all.length ? `${all.length}` : `${rows.length} of ${all.length}`} shown
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            {/* Widths declared rather than left to the browser.
                Auto layout shares the slack out evenly, so "unassigned" and a
                dash end up as wide as the names -- and the name is the thing
                being scanned for. Everything the eye does not search on is
                pinned to what it needs; the extension column absorbs the
                remainder. */}
            <colgroup>
              <col />
              <col style={{ width: 138 }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: 80 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Extension</th>
                <th>Presence</th>
                <th>Handset</th>
                <th>Cost centre</th>
                <th>Features</th>
                <th />
              </tr>
            </thead>
            {isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="arc-clickable" onClick={() => setSelected(e.id)}>
                    <td>
                      <div className="arc-row" style={{ gap: 10 }}>
                        <span className="arc-ext-num">{e.number}</span>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{e.display_name}</span>
                          {e.status !== 'active' && <StatusBadge status={e.status} />}
                        </div>
                      </div>
                    </td>
                    <td><PresenceCell ext={e} /></td>
                    <td className="arc-muted">
                      {e.registration?.user_agent
                        ? <div className="arc-stack">
                            <span className="arc-truncate">{e.registration.user_agent}</span>
                            {e.registration.contact_ip && (
                              <span className="arc-mono arc-subtle" style={{ fontSize: 11 }}>
                                {e.registration.contact_ip}
                              </span>
                            )}
                          </div>
                        : <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-muted">
                      {e.admin_group_name ?? <span className="arc-subtle">unassigned</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {e.do_not_disturb && <Badge tone="warn">DND</Badge>}
                        {e.record_mode !== 'none' && <Badge tone="info">recorded</Badge>}
                        {e.internal_only && <Badge tone="muted">internal only</Badge>}
                        {e.forward_always && <Badge tone="warn">forwarded</Badge>}
                        {!e.do_not_disturb && e.record_mode === 'none' && !e.internal_only
                          && !e.forward_always && <span className="arc-subtle">—</span>}
                      </div>
                    </td>
                    {/* Icons rather than the words "Open" and "Delete".
                        Two text buttons on every row put a second, competing
                        column of writing beside the names, and the names are
                        what the eye is scanning for. The controls stay
                        permanently visible -- only their labels moved to the
                        title and aria-label. */}
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions"
                        style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {can('extension:delete') && (
                          <button type="button"
                            className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-danger-ghost"
                            title={`Delete extension ${e.number}`}
                            aria-label={`Delete extension ${e.number}`}
                            onClick={(evt) => {
                              evt.stopPropagation();
                              if (confirm(`Delete extension ${e.number} (${e.display_name})? The handset will stop working immediately.`)) {
                                del.mutate(e.id);
                              }
                            }}>
                            <Icon name="trash" size={15} />
                          </button>
                        )}
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                          title={`Open ${e.number}`} aria-label={`Open extension ${e.number}`}
                          onClick={(evt) => { evt.stopPropagation(); setSelected(e.id); }}>
                          <Icon name="chevronRight" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {!isLoading && all.length === 0 && (
          <Empty icon="extension" title="No extensions" hint="Add one to start making calls." />
        )}
        {!isLoading && all.length > 0 && rows.length === 0 && (
          <Empty icon="search" title="Nothing matches those filters"
            hint="Clear the presence or cost centre filter to see the rest." />
        )}
      </div>
    </>
  );
}

/**
 * Three states, not two.
 *
 * "Never seen" is not the same fault as "went offline": one is a handset that
 * was never configured, the other is one that stopped. Collapsing them into
 * "not registered" sends support looking in the wrong place.
 */
function PresenceCell({ ext }: { ext: Extension }) {
  const reg = ext.registration;
  if (!reg) {
    return (
      <div className="arc-stack">
        <Badge tone="muted">never seen</Badge>
        <span className="arc-subtle" style={{ fontSize: 11 }}>no handset yet</span>
      </div>
    );
  }
  if (reg.registered) {
    return (
      <div className="arc-stack">
        <Badge tone="ok"><span className="arc-dot" aria-hidden="true" />online</Badge>
        {reg.transport && (
          <span className="arc-subtle" style={{ fontSize: 11 }}>
            {reg.transport.toUpperCase()}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="arc-stack">
      <Badge tone="danger"><span className="arc-dot" aria-hidden="true" />offline</Badge>
      <span className="arc-subtle" style={{ fontSize: 11 }}>
        {reg.last_seen_at ? `seen ${relativeTime(reg.last_seen_at)}` : 'never seen'}
      </span>
    </div>
  );
}

/**
 * Creation, with the fields that are awkward to change later.
 *
 * Not every setting — a seat has six tabs of them and a wall of inputs at
 * creation is how people stop reading. These are the ones whose default is
 * wrong often enough to be worth asking: cost centre because unallocated spend
 * is what finance chases, and internal-only because the safe answer for a new
 * seat is "cannot dial out until somebody says so".
 */
function NewExtensionForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const create = useCreateExtension(tenantId);
  const groups = useAdminGroups(tenantId);
  const [number, setNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [internalOnly, setInternalOnly] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        number, display_name: displayName,
        ...(groupId ? { admin_group_id: groupId } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      },
      { onSuccess: () => { setNumber(''); setDisplayName(''); setGroupId(''); onDone(); } },
    );
  };

  const fieldErrors = create.error instanceof ApiClientError ? create.error.fieldErrors : {};
  const generalError =
    create.error instanceof ApiClientError && Object.keys(fieldErrors).length === 0
      ? create.error.message : null;

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      <h2 className="arc-h2" style={{ marginBottom: 12 }}>New extension</h2>

      {generalError && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{generalError}</span>
        </div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: '140px minmax(0, 1.4fr) minmax(0, 1fr)',
        gap: 12, alignItems: 'start',
      }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-number">Number</label>
          <input id="ext-number" className="arc-input arc-mono" inputMode="numeric"
            placeholder="1003" required value={number}
            onChange={(e) => setNumber(e.target.value)}
            aria-invalid={!!fieldErrors['number']} />
          {fieldErrors['number']
            ? <div className="arc-error">{fieldErrors['number']}</div>
            : <div className="arc-hint">2–6 digits. 91xx is reserved.</div>}
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-name">Display name</label>
          <input id="ext-name" className="arc-input" placeholder="Reception"
            required value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            aria-invalid={!!fieldErrors['display_name']} />
          {fieldErrors['display_name'] && <div className="arc-error">{fieldErrors['display_name']}</div>}
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-group">Cost centre</label>
          <select id="ext-group" className="arc-input" value={groupId}
            onChange={(e) => setGroupId(e.target.value)}>
            <option value="">Unassigned</option>
            {groups.data?.data.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <div className="arc-hint">Where this seat's spend is reported.</div>
        </div>
      </div>

      <div className="arc-row" style={{ gap: 10, margin: '12px 0 14px' }}>
        <Switch id="ext-internal" checked={internalOnly} onChange={setInternalOnly} />
        <label htmlFor="ext-internal" style={{ fontSize: 12.5, cursor: 'pointer' }}>
          Internal calls only
          <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
            No outside line until somebody turns it on. Everything else has a
            sensible default and lives on the seat's own page.
          </span>
        </label>
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={create.isPending}>
        {create.isPending ? 'Creating…' : 'Create extension'}
      </button>
    </form>
  );
}
